import { useAccess } from '@/hooks/useAccess';
import { useIntl } from '@/i18n';
import { history } from '@/router/navigate';
import { useModuleGateStore } from '@/store/moduleGate';
import {
  isModuleGated,
  moduleGateReasonOf,
  moduleGateReasonText,
  moduleOfPath,
} from '@/utils/moduleGate';
import { LockOutlined } from '@ant-design/icons';
import { Button, Result } from 'antd';

interface ModuleGateBoxProps {
  /** 被拦下的一级模块码（`ops` / `fin`…），与路由首段同一套 */
  moduleCode: string;
}

/**
 * 模块门控拦截页（PRD `UI.gateBox`，27024–27038）。
 *
 * 两种锁要分开说，出路不同：期次未到的是「谁都还用不了」，档位不够的是「升套餐即可」。
 * 没有套餐订阅权限的人不给一个进不去的按钮，改成告诉他找谁（PRD 27032–27033）。
 */
export default function ModuleGateBox({ moduleCode }: ModuleGateBoxProps) {
  const intl = useIntl();
  const { canEnter } = useAccess();
  const rows = useModuleGateStore((s) => s.rows);
  const currentPlanName = useModuleGateStore((s) => s.currentPlanName);
  const reason = moduleGateReasonOf(rows, moduleCode);
  // 套餐页自己也可能在被门控的模块里（sys）：那时给按钮就是让他从一堵墙撞到另一堵
  const planGated = isModuleGated(rows, moduleOfPath('/sys/plan'));
  const planBlocked = planGated || reason?.kind === 'disabled';

  return (
    <Result
      icon={<LockOutlined className="text-4xl text-secondary" />}
      title={intl.formatMessage({ id: 'layout.moduleGate.title' })}
      subTitle={
        <div className="leading-relaxed">
          <div>{moduleGateReasonText(intl, reason)}</div>
          <div>
            {intl.formatMessage(
              { id: 'layout.moduleGate.currentPlan' },
              { plan: currentPlanName ?? '—' },
            )}
          </div>
        </div>
      }
      extra={
        canEnter('sys.plan') && !planBlocked ? (
          <Button type="primary" onClick={() => history.push('/sys/plan')}>
            {intl.formatMessage({ id: 'layout.moduleGate.viewPlan' })}
          </Button>
        ) : planBlocked ? null : (
          <span className="text-secondary">
            {intl.formatMessage({ id: 'layout.moduleGate.contact' })}
          </span>
        )
      }
    />
  );
}
